<template>
  <div>
    <PageHeaderBanner>
      <template #title>カタログデータダウンロード</template>
    </PageHeaderBanner>
    <div class="cdd00100-catarog-list">
      <p>
        ChemPandaに掲載している全品目のカタログデータ（SDFile）です。<br />
        カテゴリー別にまとめたデータや、新商品のみをまとめたデータがダウンロード頂けます。
      </p>
      <div :class="'chem-table-area btn-exist ' + (rows.length === 0 ? 'table-nodata' : '')">
        <q-table
          class="my-sticky-header-column-table"
          :rows="rows"
          :columns="columns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex: any, endRowIndex: any, totalRowsNumber: any) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <!-- ダウンロードHeader -->
          <template #header-cell-download="val">
            <q-th class="table-btn-area right-table-btn">{{ val.col.label }}</q-th>
          </template>
          <!-- ダウンロードBody -->
          <template #body-cell-download="cell">
            <q-td class="table-btn-area right-table-btn">
              <div class="flex">
                <q-btn
                  icon="mdi-download"
                  class="export-btn"
                  color="primary"
                  style="width: 150px"
                  @click="displayOutExcelDialog(cell.row.fileName)"
                  >ダウンロード</q-btn
                >
              </div>
            </q-td>
          </template>
        </q-table>
      </div>
    </div>
    <AlertDialog
      :display="dialogDisplayFlg"
      :level="dialogLevel"
      :confirmation="dialogConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="dialogDisplayFlg = false"
      @confirm="downloadCatalog"
      @close="dialogDisplayFlg = false"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import saveAs from 'file-saver';
import http from '../common/axios-common';
import HttpResponseStatusCode from '../common/code/HttpResponseStatusCode';
import ResultStatusCode from '../common/code/ResultStatusCode';
import DetailResultStatusCode from '../common/code/DetailResultStatusCode';
import AlertDialog from '../components/dialogs/AlertDialog.vue';
import PageHeaderBanner from '../components/banner/PageHeaderBanner.vue';

// 検索結果
interface Result {
  sdfOutZipId: number;
  category: string | null;
  version: string | null;
  itemCount: string | null;
  fileName: string;
  fileSize: string | null;
}

// カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: Array<any> = [
  {
    name: 'category',
    label: 'カテゴリー',
    align: 'left',
    field: 'category',
    required: true,
    sortable: true,
  },
  {
    name: 'version',
    label: 'Ver.',
    align: 'left',
    field: 'version',
    style: 'width: 40px',
    required: true,
    sortable: true,
  },
  {
    name: 'itemCount',
    label: '品目数',
    align: 'left',
    classes: 'text-right',
    field: 'itemCount',
    required: true,
    sortable: true,
    sort: (a: string, b: string) =>
      Number(a.split(/[^0-9]/g).join('')) - Number(b.split(/[^0-9]/g).join('')),
  },
  {
    name: 'fileName',
    label: 'ファイル名',
    align: 'left',
    field: 'fileName',
    required: true,
    sortable: true,
  },
  {
    name: 'fileSize',
    label: 'ファイルサイズ',
    align: 'left',
    classes: 'text-right',
    field: 'fileSize',
    required: true,
    sortable: true,
    sort: (a: string, b: string) =>
      Number(a.split(/[^0-9]/g).join('')) - Number(b.split(/[^0-9]/g).join('')),
  },
  {
    name: 'download',
    label: 'SDFile',
    field: 'download',
  },
];

// ダイアログのdisplayFlg(表示判断)
const dialogDisplayFlg = ref(false);

// ダイアログのlevel
const dialogLevel = ref('');

// ダイアログのtitle
const dialogTitle = ref('');

// ダイアログのtext
const dialogText = ref(['']);

// ダイアログのconfirmation(trueならキャンセルボタンを表示しない)
const dialogConfFlg = ref(true);

// formData用ファイル名
const dowFileName = ref('');

// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// 取得したデータ
const data = ref<Array<Result>>([]);

// 表示するデータ
const rows = ref<Array<Result>>(data.value);

const router = useRouter();

/**
 * SDFile出力確認ダイアログ表示
 */
const displayOutExcelDialog = (fileName: string) => {
  dialogDisplayFlg.value = true;
  dialogConfFlg.value = false;
  dialogTitle.value = 'ダウンロード確認';
  dialogLevel.value = 'information';
  dialogText.value = ['カタログデータ（SDFile）をダウンロードします。', 'よろしいですか？'];
  dowFileName.value = fileName;
};

// 初期表示
const init = () => {
  // Loading Start
  Loading.show();
  http
    .post('/cdd001001/requestCatalog')
    .then((response) => {
      data.value = response.data.catalogList;
      rows.value = data.value;
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        //  エラー画面に遷移
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

// ダウンロード失敗ダイアログを表示
const mistakeSDFDialog = () => {
  // ダイアログ表示判断をtrue(表示)に変更
  dialogDisplayFlg.value = true;
  // ダイアログレベルを設定
  dialogLevel.value = 'error';
  // ダイアログのtitleを設定
  dialogTitle.value = 'ダウンロード失敗';
  // ダイアログのtextを生成
  dialogText.value = [`選択したカタログデータが存在しません。`];
  // キャンセルボタンを表示しない
  dialogConfFlg.value = true;
};

// SDFダウンロード
const downloadCatalog = () => {
  // Loading Start
  Loading.show();
  const formData = new FormData();
  formData.append('fileName', dowFileName.value);

  http
    .post('/cdd001001/downloadCatalog', formData)
    .then((response) => {
      switch (response.data.common.status) {
        // 結果ステータス：正常終了
        case ResultStatusCode.SUCCESS:
          switch (response.data.common.detail_status) {
            // 詳細結果ステータス：正常終了
            case DetailResultStatusCode.SUCCESS:
              // SDFダウンロード
              saveAs(response.data.path, response.data.fileName);
              // ダイアログ表示
              dialogTitle.value = 'ダウンロード開始';
              dialogLevel.value = 'information';
              dialogText.value = [
                'カタログデータ（SDFile）のダウンロードを開始しました。',
                '※ファイルサイズが大きい場合、ダウンロードに時間がかかりますのでしばらくお待ちください。',
              ];
              dialogConfFlg.value = true;
              dialogDisplayFlg.value = true;
              break;
            // SDFダウンロード失敗ダイアログボックス表示
            case DetailResultStatusCode.FILE_NOT_EXIST_ERROR:
              mistakeSDFDialog();
              break;
            default:
              break;
          }
          break;
        // 結果ステータス：異常終了
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
          break;
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        //  エラー画面に遷移
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// 初期表示
init();
</script>
<style scoped>
.cdd00100-catarog-list {
  width: 80%;
  margin: 0 auto;
}
.cdd00100-comment {
  margin: 10px 0;
}

.chem-table-area.btn-exist .table-btn-area.right-table-btn {
  width: 156px;
}

.text-link {
  cursor: pointer;
  color: var(--q-pitchBlack);
  text-decoration: none;
  border-bottom: 1px solid var(--q-pitchBlack);
}

.text-link:hover {
  color: var(--q-primary);
  border-color: var(--q-primary);
}
</style>
<style>
.cdd00100-catarog-list .q-table__container {
  margin-bottom: 0 !important;
}
</style>
